<template>
    <div>
        <header class="explorer-header">
            <div class="header-left">
                <slot name="header-left"><span class="explorer-title">Explore our music list</span></slot>
            </div>
            <div class="header-right">
                <slot name="header-right">
                    <div class="loading-container">
                        <v-progress-circular indeterminate color="deep-orange" v-if="loading"></v-progress-circular>
                    </div>
                    <v-text-field
                        :loading="loading"
                        dense
                        outlined
                        v-model="filters.search"
                        placeholder="Search Music"
                        :debounce="250"
                        class="search-field"
                        clearable
                    >
                        <template v-slot:prepend-inner>
                            <font-awesome-icon icon="fa-regular fa-magnifying-glass"></font-awesome-icon>
                        </template>
                    </v-text-field>
                    <v-select
                        ref="genreSelect"
                        dense
                        :placeholder="filters.favoritesOnly ? 'Genres (Staff picks)' : 'Genres'"
                        class="explore-header-field half"
                        outlined
                        clearable
                        :items="genres"
                        item-text="name"
                        item-value="id"
                        multiple
                        v-model.lazy="filters.selectedGenres"
                    >
                        <template v-slot:prepend-item v-if="$vuetify.breakpoint.smAndDown">
                            <v-list-item>
                                <v-list-item-action start>
                                    <v-switch v-model="filters.favoritesOnly" size="sm" color="orange"></v-switch>
                                </v-list-item-action>
                                <v-list-item-title> Show staff picks only </v-list-item-title>
                            </v-list-item>
                            <v-list-item>
                                <v-list-item-content>
                                    <v-list-item-title> Filter by Genre </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                        <template v-slot:prepend-item v-else>
                            <v-list-item>
                                <v-list-item-action start>
                                    <v-switch v-model="filters.favoritesOnly" size="sm" color="orange"></v-switch>
                                </v-list-item-action>
                                <v-list-item-title> Show staff picks only </v-list-item-title>
                            </v-list-item>
                        </template>
                        <template v-slot:append-item v-if="$vuetify.breakpoint.smAndDown">
                            <v-list-item>
                                <v-list-item-content>
                                    <v-btn color="orange">Apply Filter</v-btn>
                                </v-list-item-content>
                            </v-list-item>
                        </template>
                        <template v-slot:selection="{ item, index }">
                            <v-chip small v-if="index === 0">
                                <span>{{ item.name }}</span>
                            </v-chip>
                            <span v-if="index === 1" class="grey--text text-caption">
                                (+{{ filters.selectedGenres.length - 1 }})
                            </span>
                        </template>
                    </v-select>
                    <v-select
                        ref="keywordSelect"
                        dense
                        class="explore-header-field half"
                        outlined
                        placeholder="Keywords"
                        clearable
                        :items="keywords"
                        item-text="name"
                        item-value="id"
                        multiple
                        v-model.lazy="filters.selectedKeywords"
                    >
                        <template v-slot:selection="{ item, index }">
                            <v-chip small v-if="index === 0">
                                <span>{{ item.name }}</span>
                            </v-chip>
                            <span v-if="index === 1" class="grey--text text-caption">
                                (+{{ filters.selectedKeywords.length - 1 }})
                            </span>
                        </template>
                    </v-select>
                    <TributeMusicUploader
                        ref="uploader"
                        :hideButton="true"
                        :token="token"
                        :funeralHomeId="$props.funeralHomeId"
                        @upload-success="onAddCustomMusic"
                        open-to-file-explorer
                        v-if="token"
                    >
                        <template v-slot:activator="{ open }">
                            <v-btn outlined class="custom-upload" @click="open">
                                <v-icon left> mdi-upload </v-icon>
                                Upload your own
                            </v-btn>
                        </template>
                    </TributeMusicUploader>
                </slot>
            </div>
        </header>
        <main class="track-list py-2">
            <template v-for="song in songs">
                <MusicTrack
                    class="pointer"
                    :ref="`musicTrackTile-${song.id}`"
                    @click="handleTrackTileClick(song)"
                    :song="song"
                    :key="song.id"
                    :isSelected="selectedSongsIds.includes(song.id)"
                    :playlistType="playlistType"
                    :token="token"
                />
            </template>
            <div class="no-results" v-if="!loading && !songs.length">
                <div>No results to display.</div>
            </div>
            <div class="no-results" v-else-if="loading">
                <div>Loading</div>
            </div>
        </main>
        <div class="pagination">
            <v-pagination v-model="currentPage" :length="totalPages" :total-visible="6"></v-pagination>
        </div>
    </div>
</template>
<script>
import { mapActions } from 'vuex';
import { map, debounce } from 'lodash';
import TributeMusicUploader from '@/components/ManageService/Tribute/TributeMusicUploader.vue';
import CustomTooltip from '@/components/ui/CustomTooltip.vue';
import MusicTrack from './MusicTrack.vue';
import TributeSongService from '@/services/tributeSong.service';
import { playlistTypes } from '../../../constants';

export default {
    name: 'MusicExplorer',
    data() {
        return {
            laurelIcon: require(`@/assets/icons/laurel.svg`),
            tributeSongApiInstance: null,
            token: null,
            message: '',
            total: 0,
            currentPage: 1,
            pageSize: 15,
            loading: false,
            keywords: [],
            filters: {
                search: '',
                selectedKeywords: [],
                selectedGenres: [],
                favoritesOnly: false,
            },
            genres: [],
            transitionDuration: 1,
            templateDuration: 0,
            songs: [],
        };
    },
    inject: ['state'],
    components: {
        // draggable,
        // TributeMusicTable,
        // TributePlaylistExpansion,
        MusicTrack,
        TributeMusicUploader,
        CustomTooltip,
    },
    filters: {
        durationTimeStamp(val) {
            var minutes = Math.floor(val / 60);
            var seconds = val - minutes * 60;

            function str_pad_left(string, pad, length) {
                return (new Array(length + 1).join(pad) + string).slice(-length);
            }
            const finalTime = str_pad_left(minutes, '0', 2) + ':' + str_pad_left(seconds, '0', 2);
            return finalTime;
        },
    },
    computed: {
        totalPages() {
            return Math.ceil(this.total / this.pageSize);
        },
        selectedSongs: {
            get() {
                return this.playlistType === playlistTypes.PUBLIC
                    ? this.$store.state.tributeVideo.publicPlaylist
                    : this.$store.state.tributeVideo.originalPlaylist;
            },
        },
        selectedSongsIds() {
            if (this.selectedSongs?.length) {
                return map(this.selectedSongs, 'id');
            }
            return [];
        },
        selectedTemplates: {
            get() {
                return this.$store.state.tributeVideo.selectedTemplates;
            },
            set(value) {
                this.updateTributeVideoSelectedTemplates(value);
            },
        },
        playlistDuration() {
            let duration = 0;
            this.selectedSongs.forEach(song => {
                duration += song.duration;
            });
            return duration;
        },
        endingDuration() {
            let duration = 10;

            if (this.$store.state.tributeVideo.endingType == 2) {
                duration = this.$store.state.tributeVideo.endingDuration;
            }

            return duration;
        },
        recommendedDuration() {
            return this.templateDuration + this.slideshowDuration + this.endingDuration;
        },
        slideshowDuration() {
            // return (
            //     (this.tributeVideo.totalPhotos + 1) * this.tributeVideo.slideDuration - this.tributeVideo.totalPhotos
            // );
            const videoCount = this.$store.state.tributeVideo.videoSlidesCount;

            // current transition time is 1s, account for 1s overlap per slide during duration calc
            const totalSlides = this.$store.state.tributeVideo.totalPhotos - videoCount;

            let fullPhoto = totalSlides * this.$store.state.tributeVideo.slideDuration;
            let adjustedPhoto = fullPhoto - totalSlides * this.transitionDuration;

            let fullVideo = this.$store.state.tributeVideo.videoSlidesDuration;
            let adjustedVideo = fullVideo - videoCount * this.transitionDuration;

            let total = adjustedPhoto + adjustedVideo;
            return parseFloat(total.toFixed(2));
        },
        remainingSongs() {
            const averageSongLength = 210;
            const remainingTime = this.recommendedDuration - this.playlistDuration;
            return Math.round(remainingTime / averageSongLength);
        },
        tributeVideo() {
            return this.$store.state.tributeVideo;
        },
    },
    props: {
        filteredGenres: {
            type: Array,
            required: false,
        },
        featuredOnly: {
            type: Boolean,
            default: false,
        },
        funeralHomeId: {
            type: Number,
            required: false,
        },
        multiPlaylist: {
            type: Boolean,
            default: false,
        },
        playlistType: {
            type: Number,
            default: 0,
        },
    },
    watch: {
        filteredGenres() {
            this.selectedGenres = [...this.filteredGenres];
        },
        filters: {
            handler() {
                this.currentPage = 1;
                this.debouncedDataFetch();
            },
            deep: true,
        },
        currentPage() {
            this.debouncedDataFetch();
        },
    },
    methods: {
        ...mapActions(['showSnackbar'], 'tributeVideo', [
            'updateTributeVideo',
            'updateTributeVideoSelectedTemplates',
            'updateTributeVideoSelectedSongs',
        ]),
        buildQueryFilters() {
            let filters = {};
            let queryFilters = {};
            filters.pageSize = this.pageSize;
            filters.pageNumber = this.currentPage - 1;

            if (this.filters.search?.length) {
                filters.search = this.filters.search;
            }

            if (this.funeralHomeId) {
                filters.funeralHomeId = this.funeralHomeId;
            }

            // TODO: keywords & genres
            if (this.filters.selectedGenres?.length) {
                queryFilters.genre = this.filters.selectedGenres;
            }
            if (this.filters.selectedKeywords?.length) {
                queryFilters.keyword = this.filters.selectedKeywords;
            }
            if (this.filters.favoritesOnly) {
                filters.favoritesOnly = true;
            }

            return { filters, queryFilters };
        },
        handleTrackTileClick(song) {
            if (this.selectedSongsIds.includes(song.id)) {
                this.showSnackbar({ message: 'Track already selected' });
                return;
            }

            const refName = `musicTrackTile-${song.id}`;
            const tileRef = this.$refs[refName];

            let ref = null;

            if (Array.isArray(tileRef) && tileRef.length > 0) {
                ref = tileRef[0];
            } else if (tileRef) {
                ref = tileRef;
            }

            if (ref && typeof ref.addSong === 'function') {
                ref.addSong();
                return;
            }
        },
        onAddCustomMusic(track) {
            const updatedTracks = [...this.selectedSongs, track];
            // Custom song has been added, now we need to add it to the tribute video's selected songs
            this.tributeSongApiInstance
                .updateSelectedSongs(this.$store.state.tributeVideo.id, { selectedSongs: map(updatedTracks, 'id') })
                .then(_updatedSongResults => {
                    // add the new track to the tributeEditor vuex store

                    this.$store.dispatch('tributeVideo/updateTributeVideoPlaylist', {
                        playlistType: this.playlistType,
                        songs: updatedTracks,
                    });

                    this.$emit('customSongAdded');
                    this.busy = false;
                })
                .catch(() => {
                    this.showSnackbar({ message: 'Error adding song. Please try again later', color: 'error' });
                    this.busy = false;
                });
        },
        getKeywords() {
            return this.tributeSongApiInstance
                .getKeywords()
                .then(res => {
                    this.keywords = res.data.musicKeywords;
                    this.totalKeywords = res.data.total;
                })
                .catch(err => {
                    console.log(err, 'error');
                });
        },
        debouncedDataFetch: debounce(function () {
            this.getSongs();
        }, 700),
        getSongs() {
            this.loading = true;
            const { filters, queryFilters } = this.buildQueryFilters();

            return this.tributeSongApiInstance.getSongs(filters, queryFilters).then(songResults => {
                this.total = songResults.data?.total;
                this.songs = songResults.data?.tributeSongs || [];
                this.$nextTick(() => {
                    this.loading = false;
                });
            });
        },
        async getGenres() {
            let totalUploads = 0;
            if (this.funeralHomeId) {
                totalUploads = await this.getCustomUploadsCount(this.funeralHomeId);
            }

            return this.tributeSongApiInstance
                .getGenres()
                .then(res => {
                    this.genres = res.data.musicGenres;
                    this.totalGenres = res.data.total;

                    if (totalUploads > 0) {
                        //Specail case: Upload 'genre' mapped to id -1
                        this.genres.unshift({ name: 'Uploads', image: null, id: -1 });
                        this.totalGenres++;
                    }
                })
                .catch(err => {
                    console.log(err, 'error');
                });
        },
        async setAuthToken() {
            const response = await this.$auth.getIdTokenClaims();
            this.token = response.__raw;
        },
        setFilter(type, value) {
            if (value) {
                switch (type) {
                    case 'favoritesOnly':
                        this.filters.favoritesOnly = value;
                        break;
                    case 'genre':
                        this.filters.selectedGenres = [value];
                        break;
                    case 'keyword':
                        this.filters.selectedKeywords = [value];
                        break;
                }
            } else {
                // If a user clicks on "Explore All Music" then we clear out existing filters
                this.filters.selectedGenres = [];
                this.filters.selectedKeywords = [];
                this.filters.favoritesOnly = false;
            }
        },
        async getCustomUploadsCount(funeralHomeId) {
            if (!funeralHomeId) return 0;

            try {
                const resp = await this.tributeSongApiInstance.getCustomUploadsCount(funeralHomeId);
                if (resp.data.total) {
                    return resp.data.total;
                }
                return 0;
            } catch (error) {
                console.log(error, 'error getting songs');
            }
        },
        // setSlideDuration() {
        //     let slideDuration = 0;
        //     if (this.tributeVideo.totalPhotos + 1 < 100) {
        //         slideDuration = 7;
        //     } else {
        //         slideDuration = 5;
        //     }

        //     this.updateTributeVideo({ slideDuration: slideDuration });
        // },
    },
    async mounted() {
        if (this.state.token) {
            this.token = this.state.token;
        } else {
            await this.setAuthToken();
        }
        this.tributeSongApiInstance = TributeSongService(this.token);

        // if (this.tributeVideo.slideDuration == 0) {
        //     this.setSlideDuration();
        // }
        this.getSongs();
        this.getKeywords();
        this.getGenres();
    },
};
</script>

<style lang="scss" scoped>
.explorer-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 10px;
    position: sticky;
    top: -5px;
    background: white;
    z-index: 20;
    @include mobile {
        flex-direction: column;
        padding-bottom: 2px;
    }
    .header-left {
        .explorer-title {
            font-weight: bold;
            text-transform: uppercase;
            font-size: 12px;
            color: $primary-grey;
        }
    }
    .header-right {
        display: flex;
        gap: 10px;
        @include mobile() {
            gap: 6px;
            flex-direction: column;
            align-self: stretch;
            flex-wrap: wrap;
            flex-direction: row;

            & > *:not(.half) {
                width: 100%;
            }
            .half {
                flex-basis: 48%;
            }
            .custom-upload {
                width: 100%;
            }

            @include landscape() {
                flex-direction: row;
                flex-wrap: wrap;
                .search-field {
                    max-width: 20%;
                }
                & > *:not(.half):not(.search-field) {
                    max-width: 23%;
                }
                .half {
                    flex-basis: 18%;
                }
                .loading-container {
                    width: 40px;
                }
            }
        }
    }
}
// tweak to shift the search field icon down a bit for better alignment
.search-field ::v-deep .v-input__prepend-inner {
    padding-top: 3px;
}
.custom-upload {
    height: 40px !important;
    & ::v-deep .v-btn__content {
        text-transform: initial;
        letter-spacing: normal;
    }
}
.explore-header-field {
    width: 240px;
    @include mobile {
        max-width: inherit;
    }
}
.track-list {
    display: flex;
    flex-direction: row;
    gap: 10px;
    flex-wrap: wrap;

    .track {
        flex-basis: 32%;
        max-width: 32%;
    }
}
.no-results {
    padding: 1rem;
    font-weight: 500;
    background: #f9fafb;
    text-align: center;
    width: 100%;
}
.pagination {
    justify-content: flex-end;

    & ::v-deep .v-pagination__item--active {
        background: $btn-orange !important;
    }
}

@media only screen and (min-width: 1400px) {
    .track-list {
        .track {
            flex-basis: 24%;
            max-width: 24%;
        }
    }
}
@media only screen and (max-width: 1200px) {
    .track-list {
        .track {
            flex-basis: 49%;
            max-width: 49%;
        }
    }
}
@media only screen and (max-width: 800px) {
    .track-list {
        .track {
            flex-basis: 100%;
            max-width: 100%;
            @include landscape() {
                flex-basis: 49%;
                max-width: 49%;
            }
        }
    }
}
</style>
